import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { formatAmount } from './price'

/** Tall enough for the 40px numeral, so the quoted plan's words sit level with it. */
const PRICE_ROW_MIN_HEIGHT = 44

type PriceTagProps = {
  /** null on the plan that is quoted rather than listed. */
  amount: number | null
  /** 'month' for a subscription, null for a package bought once. */
  period: 'month' | null
  /** The line under the number: what the money buys, in respondents. */
  caption?: string
}

/**
 * One price, read in a second: a big number with its unit beside it and
 * nothing competing. The plan that has no list price says so in words instead
 * of inventing a "from" figure, but it is set like a price rather than like a
 * sentence, so the three cards can be read across.
 *
 * Both the amount row and the caption carry a minimum height. Without them the
 * quoted plan's shorter block pulls its button and its whole feature list up,
 * and three cards that cannot be scanned across a row are not a price list.
 */
export default function PriceTag({ amount, period, caption }: PriceTagProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')

  return (
    <Box>
      {amount === null ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', minHeight: PRICE_ROW_MIN_HEIGHT }}>
          <Typography
            sx={{
              fontFamily: theme.affexy.fonts.heading,
              fontSize: 30,
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
            }}
          >
            {t('price.custom')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.75,
            flexWrap: 'wrap',
            minHeight: PRICE_ROW_MIN_HEIGHT,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: theme.affexy.fonts.heading,
              fontSize: 40,
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatAmount(amount)}
          </Typography>
          <Typography component="span" sx={{ fontSize: 14, color: c.ink56 }}>
            {t(period === 'month' ? 'price.unitPerMonth' : 'price.unit')}
          </Typography>
        </Box>
      )}
      <Typography sx={{ mt: 0.5, fontSize: 13, color: c.ink56, minHeight: 19 }}>
        {caption ?? ''}
      </Typography>
    </Box>
  )
}
